import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext';
import { getMyOrders } from '../services/api';
import CloudflareImage from '../components/ui/cloudflare-image';
import PageLayout from '../components/PageLayout';

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  margin: 0;
  text-transform: uppercase;
`;

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OrderCard = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const OrderHeader = styled.div`
  background-color: #f8f8f8;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e5e5;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderId = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const OrderDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const OrderStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusLabel = styled.span`
  font-size: 12px;
  color: #666;
`;

const StatusValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${props => {
    switch(props.status) {
      case 'Delivered':
        return '#28a745';
      case 'Shipped':
        return '#007bff';
      case 'Processing':
        return '#ffc107';
      case 'Cancelled':
        return '#dc3545';
      default:
        return '#000';
    }
  }};
`;

const OrderItems = styled.div`
  padding: 0;
`;

const OrderItem = styled.div`
  display: flex;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ItemImage = styled.div`
  width: 80px;
  height: 100px;
  margin-right: 16px;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const ItemVariant = styled.div`
  font-size: 12px;
  color: #666;
`;

const ItemPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
`;

const OrderFooter = styled.div`
  background-color: #f8f8f8;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e5e5e5;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const OrderTotal = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const PrimaryButton = styled(Button)`
  background-color: #000;
  color: #fff;
  border: none;
  
  &:hover {
    background-color: #333;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: #000;
  border: 1px solid #000;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const EmptyOrders = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const EmptyMessage = styled.p`
  font-size: 18px;
  color: #666;
  margin-bottom: 24px;
`;

const ShopButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  background-color: #000;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.2s;
  
  &:hover {
    background-color: #333;
  }
`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const params = {
          sort: sortBy,
          status: statusFilter,
        };
        const response = await getMyOrders(params);
        if (response.success) {
          setOrders(response.orders);
        } else {
          setError('Failed to fetch orders.');
        }
      } catch (err) {
        setError('An error occurred while fetching orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [sortBy, statusFilter]);

  if (loading) {
    return (
        <PageLayout>
            <ContentWrapper>
                <p>Loading your orders...</p>
            </ContentWrapper>
        </PageLayout>
    );
  }

  if (error) {
    return (
        <PageLayout>
            <ContentWrapper>
                <p className="text-red-500">{error}</p>
            </ContentWrapper>
        </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ContentWrapper>
        <PageHeader>
          <Title>My Orders</Title>
          <div className="flex items-center gap-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="Processing">Processing</option>
              <option value="Shipped">Shipped</option>
              <option value="Delivered">Delivered</option>
              <option value="Returned">Returned</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
        </PageHeader>
        
        {orders.length > 0 ? (
          <OrdersContainer>
            {orders.map(order => (
              <OrderCard key={order._id}>
                <OrderHeader>
                  <OrderInfo>
                    <OrderId>Order #{order.orderNumber}</OrderId>
                    <OrderDate>Placed on {new Date(order.createdAt).toLocaleString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}</OrderDate>
                  </OrderInfo>
                  <OrderStatus>
                    <StatusLabel>Status:</StatusLabel>
                    <StatusValue status={order.orderStatus}>{order.orderStatus}</StatusValue>
                  </OrderStatus>
                </OrderHeader>
                
                <OrderItems>
                  {order.orderItems.map(item => (
                    <OrderItem key={item._id}>
                      <ItemImage>
                        <CloudflareImage src={item.image} alt={item.name} onError={(e) => {
                          e.target.src = '/images/placeholder.jpg';
                        }} />
                      </ItemImage>
                      <ItemDetails>
                        <ItemName>{item.name}</ItemName>
                        {item.size !== 'OneSize' && <ItemVariant>Size: {item.size}</ItemVariant>}
                        <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>
                      </ItemDetails>
                    </OrderItem>
                  ))}
                </OrderItems>
                
                <OrderFooter>
                  <OrderTotal>Total: £{order.totalPrice.toFixed(2)}</OrderTotal>
                  <ActionButtons>
                    {order.orderStatus === 'Delivered' && (
                      <SecondaryButton>Write Review</SecondaryButton>
                    )}
                    <Link to={`/order/${order.orderNumber}`} style={{ textDecoration: 'none' }}>
                      <PrimaryButton>View Details</PrimaryButton>
                    </Link>
                  </ActionButtons>
                </OrderFooter>
              </OrderCard>
            ))}
          </OrdersContainer>
        ) : (
          <EmptyOrders>
            <EmptyMessage>You haven't placed any orders yet.</EmptyMessage>
            <ShopButton to="/shop">Start Shopping</ShopButton>
          </EmptyOrders>
        )}
      </ContentWrapper>
    </PageLayout>
  );
};

export default OrdersPage;
